import { useEffect, useMemo, useState, type FormEvent, type DragEvent } from "react";
import {
  Apple,
  Check,
  Clock,
  Copy,
  Download,
  FileCode2,
  HardDrive,
  Plus,
  QrCode,
  RefreshCw,
  Search,
  Smartphone,
  Target,
  Trash2,
  Upload,
  UploadCloud,
  X,
} from "lucide-react";
import { Pill } from "../components/StatusPill.js";
import { useToast } from "../components/Toast.js";
import type {
  AppSummary,
  BuildKind,
  BuildSummary,
  Platform,
  ShareBuildResponse,
  UploadBuildRequest,
} from "../contract.js";
import { apiErrorMessage } from "../lib/api-error.js";
import { copyText } from "../lib/clipboard.js";
import { actorName, formatBytes, formatDateTime } from "../lib/format.js";
import { matchesTerm, useScope } from "../lib/scope.js";
import {
  useDeleteBuildMutation,
  useGetSetupQuery,
  useListAppsQuery,
  useListBuildsQuery,
  usePromoteBuildMutation,
  useShareBuildMutation,
} from "../store/api.js";
import { startBuildUpload } from "../store/upload-manager.js";
import { useAppDispatch, useAppSelector } from "../store/index.js";
import { QrImage } from "../components/QrImage.js";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const PAGE_SIZE = 25;
const SHA_PREFIX = 12;

const PLATFORM_LABEL: Record<Platform, string> = {
  android: "Android",
  ios: "iOS",
};

const BINARY_KINDS: Record<string, { kind: BuildKind; platform: Platform }> = {
  apk: { kind: "apk", platform: "android" },
  aab: { kind: "aab", platform: "android" },
  ipa: { kind: "ipa", platform: "ios" },
};

function deriveBinary(filename: string): { kind: BuildKind; platform: Platform } | null {
  const dot = filename.lastIndexOf(".");
  if (dot < 0) return null;
  return BINARY_KINDS[filename.slice(dot + 1).toLowerCase()] ?? null;
}

export function BuildsPage() {
  const role = useAppSelector((state) => state.auth.user?.role);
  const canRelease = role === "admin" || role === "releaser";
  const toast = useToast();
  const scope = useScope();

  const apps = useListAppsQuery();
  const platform = scope.get("platform") as Platform | "";
  const page = Number(scope.get("page") || 0);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<BuildSummary | null>(null);
  const [qrTarget, setQrTarget] = useState<{ build: BuildSummary; url: string } | null>(null);
  const [promoteTarget, setPromoteTarget] = useState<BuildSummary | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const [shared, setShared] = useState<Record<string, ShareBuildResponse>>({});
  const [shareBuild] = useShareBuildMutation();
  const [deleteBuild, { isLoading: isDeleting }] = useDeleteBuildMutation();

  useEffect(() => {
    if (scope.app || !apps.data || apps.data.length === 0) return;
    scope.set({ app: apps.data[0].slug }, { replace: true });
  }, [apps.data, scope]);

  const selectedApp = apps.data?.find((entry) => entry.slug === scope.app);
  const channels = selectedApp?.channels ?? [];

  const builds = useListBuildsQuery(
    {
      app: scope.app || undefined,
      channel: scope.channel || undefined,
      platform: platform || undefined,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    },
    { skip: !scope.app }
  );

  const rows = builds.data?.builds ?? [];
  const total = builds.data?.total ?? 0;
  const lastPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);

  const visible = useMemo(
    () =>
      rows.filter((row) =>
        matchesTerm(
          scope.q,
          row.version,
          row.buildNumber,
          row.filename,
          row.sha256,
          row.fingerprint,
          row.notes,
          row.uploadedBy
        )
      ),
    [rows, scope.q]
  );

  // Counts for metric cards
  const androidCount = rows.filter((r) => r.platform === "android").length;
  const iosCount = rows.filter((r) => r.platform === "ios").length;

  async function mintLink(build: BuildSummary): Promise<ShareBuildResponse | null> {
    const result = await shareBuild(build.id);
    if ("error" in result) {
      toast(apiErrorMessage(result.error), "danger");
      return null;
    }
    setShared((current) => ({ ...current, [build.id]: result.data }));
    return result.data;
  }

  async function handleCopyLink(build: BuildSummary) {
    const link = await mintLink(build);
    if (!link) return;
    try {
      await copyText(link.url);
      setCopiedId(build.id);
      setTimeout(() => setCopiedId(null), 2500);
      toast("Tautan unduh berhasil disalin (berlaku permanen tanpa masa kadaluarsa).");
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : String(cause), "danger");
    }
  }

  async function handleShowQr(build: BuildSummary) {
    const link = await mintLink(build);
    if (!link) return;
    setQrTarget({ build, url: link.url });
  }

  async function handleDownload(build: BuildSummary) {
    const link = await mintLink(build);
    if (!link) return;
    window.location.assign(link.url);
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    const result = await deleteBuild(deleteTarget.id);
    if ("error" in result) {
      toast(apiErrorMessage(result.error), "danger");
    } else {
      toast(`Biner ${deleteTarget.filename} berhasil dihapus.`);
      setDeleteTarget(null);
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#101828]">Upload APK & IPA</h1>
          <p className="text-[13.5px] text-[#667085]">
            Unggah dan distribusikan biner Android (.apk, .aab) dan iOS (.ipa) untuk pengujian internal tim dan QA.
          </p>
        </div>

        {canRelease && (
          <Button
            className="h-9 gap-1.5 bg-[#0F1B3D] hover:bg-[#1D2939] text-white shadow-xs text-xs font-medium self-start sm:self-auto"
            onClick={() => setUploadOpen(true)}
            disabled={!apps.data || apps.data.length === 0}
          >
            <Plus className="size-4" />
            Unggah Biner Pengujian
          </Button>
        )}
      </div>

      {/* 4 Stat Cards */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <div className="rounded-xl border border-[#EAECF0] bg-white p-4 shadow-xs">
          <div className="flex items-center justify-between text-xs font-medium text-[#667085]">
            <span>Total Biner</span>
            <HardDrive className="size-4 text-[#98A2B3]" />
          </div>
          <div className="mt-2 text-2xl font-bold tracking-tight text-[#101828] font-mono">
            {total || rows.length}
          </div>
          <span className="mt-1 block text-[11px] text-[#667085]">Berkas siap uji</span>
        </div>

        <div className="rounded-xl border border-[#EAECF0] bg-white p-4 shadow-xs">
          <div className="flex items-center justify-between text-xs font-medium text-[#667085]">
            <span>Android (APK / AAB)</span>
            <Smartphone className="size-4 text-[#17B26A]" />
          </div>
          <div className="mt-2 text-2xl font-bold tracking-tight text-[#101828] font-mono">
            {androidCount}
          </div>
          <span className="mt-1 block text-[11px] text-[#667085]">Paket Android</span>
        </div>

        <div className="rounded-xl border border-[#EAECF0] bg-white p-4 shadow-xs">
          <div className="flex items-center justify-between text-xs font-medium text-[#667085]">
            <span>iOS (IPA)</span>
            <Apple className="size-4 text-[#2563EB]" />
          </div>
          <div className="mt-2 text-2xl font-bold tracking-tight text-[#101828] font-mono">
            {iosCount}
          </div>
          <span className="mt-1 block text-[11px] text-[#667085]">Paket TestFlight / Ad-Hoc</span>
        </div>

        <div className="rounded-xl border border-[#EAECF0] bg-white p-4 shadow-xs">
          <div className="flex items-center justify-between text-xs font-medium text-[#667085]">
            <span>Kanal Aktif</span>
            <span className="size-2 rounded-full bg-[#17B26A]" />
          </div>
          <div className="mt-2 text-2xl font-bold tracking-tight text-[#101828] font-mono">
            {channels.length || 2}
          </div>
          <span className="mt-1 block text-[11px] text-[#667085]">Target distribusi</span>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="flex flex-col gap-3 rounded-xl border border-[#EAECF0] bg-white p-3.5 shadow-xs sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          {/* Platform Segmented Tabs */}
          <div className="inline-flex rounded-lg border border-[#EAECF0] bg-[#F6F7F9] p-0.5 text-xs">
            <button
              type="button"
              onClick={() => scope.set({ platform: undefined, page: undefined })}
              className={`rounded-md px-3 py-1 font-medium transition-colors ${
                !platform ? "bg-white text-[#101828] shadow-2xs font-semibold" : "text-[#667085] hover:text-[#101828]"
              }`}
            >
              Semua Platform
            </button>
            <button
              type="button"
              onClick={() => scope.set({ platform: "android", page: undefined })}
              className={`rounded-md px-3 py-1 font-medium transition-colors ${
                platform === "android"
                  ? "bg-white text-[#101828] shadow-2xs font-semibold"
                  : "text-[#667085] hover:text-[#101828]"
              }`}
            >
              Android
            </button>
            <button
              type="button"
              onClick={() => scope.set({ platform: "ios", page: undefined })}
              className={`rounded-md px-3 py-1 font-medium transition-colors ${
                platform === "ios"
                  ? "bg-white text-[#101828] shadow-2xs font-semibold"
                  : "text-[#667085] hover:text-[#101828]"
              }`}
            >
              iOS
            </button>
          </div>

          {/* Channel Filter */}
          <select
            className="h-8 rounded-lg border border-[#EAECF0] bg-white px-2.5 text-xs text-[#344054] focus:outline-none focus:border-[#2563EB]"
            value={scope.channel}
            onChange={(e) => scope.set({ channel: e.target.value, page: undefined })}
          >
            <option value="">Semua kanal</option>
            {channels.map((ch) => (
              <option key={ch.id} value={ch.name}>
                {ch.name}
              </option>
            ))}
          </select>
        </div>

        {/* Search Input & Refresh */}
        <div className="flex items-center gap-2">
          <div className="relative flex-1 sm:w-64">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-[#98A2B3]" />
            <Input
              value={scope.q}
              onChange={(e) => scope.set({ q: e.target.value })}
              placeholder="Cari versi, berkas, atau catatan…"
              className="h-8 pl-8 text-xs bg-white border-[#EAECF0] rounded-lg"
            />
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={() => void builds.refetch()}
            disabled={!scope.app || builds.isFetching}
            className="h-8 border-[#EAECF0] text-xs text-[#344054]"
            title="Segarkan daftar"
          >
            <RefreshCw className={`size-3.5 ${builds.isFetching ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {/* Binaries Table */}
      <div className="rounded-xl border border-[#EAECF0] bg-white shadow-xs overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="border-b border-[#EAECF0] bg-[#F6F7F9]/70 text-[#667085]">
                <th className="py-3 px-4 font-medium">Biner & Berkas</th>
                <th className="py-3 px-4 font-medium">Versi</th>
                <th className="py-3 px-4 font-medium">Kanal</th>
                <th className="py-3 px-4 font-medium">Ukuran</th>
                <th className="py-3 px-4 font-medium">SHA-256</th>
                <th className="py-3 px-4 font-medium">Diunggah Oleh</th>
                <th className="py-3 px-4 font-medium">Waktu</th>
                <th className="py-3 px-4 font-medium text-right">Aksi Pengujian</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#EAECF0]">
              {visible.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-12 text-center text-[#667085]">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <HardDrive className="size-8 text-[#D0D5DD]" />
                      <p className="font-medium text-[#101828]">Belum ada biner pengujian</p>
                      <p className="text-xs text-[#667085]">
                        Unggah APK atau IPA pertama Anda untuk dibagikan kepada penguji QA.
                      </p>
                      {canRelease && (
                        <Button
                          size="sm"
                          onClick={() => setUploadOpen(true)}
                          className="mt-2 bg-[#0F1B3D] text-white text-xs"
                        >
                          + Unggah biner sekarang
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                visible.map((build) => {
                  const isAndroid = build.platform === "android";
                  const ext = build.filename.split(".").pop()?.toUpperCase() ?? "BIN";

                  return (
                    <tr key={build.id} className="hover:bg-[#F6F7F9] transition-colors">
                      {/* Biner & Format */}
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2.5">
                          <div
                            className={`size-8 rounded-lg flex items-center justify-center shrink-0 ${
                              isAndroid ? "bg-[#ECFDF3] text-[#17B26A]" : "bg-[#EFF4FF] text-[#2563EB]"
                            }`}
                          >
                            {isAndroid ? <Smartphone className="size-4" /> : <Apple className="size-4" />}
                          </div>
                          <div className="min-w-0 max-w-[260px]">
                            <span className="block font-medium text-[#101828] break-words whitespace-normal leading-snug">
                              {build.filename}
                            </span>
                            <span className="inline-block mt-1 text-[10px] font-semibold tracking-wider text-[#667085] bg-[#EAECF0] px-1.5 py-0.5 rounded">
                              .{ext.toLowerCase()}
                            </span>
                          </div>
                        </div>
                      </td>

                      {/* Versi & Build */}
                      <td className="py-3 px-4">
                        <span className="font-mono font-bold text-[#101828]">v{build.version}</span>
                        {build.buildNumber && (
                          <span className="block text-[11px] font-mono text-[#667085]">
                            build {build.buildNumber}
                          </span>
                        )}
                      </td>

                      {/* Kanal */}
                      <td className="py-3 px-4">
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#EFF4FF] border border-[#BFDBFE] px-2.5 py-0.5 text-[11px] font-medium text-[#2563EB]">
                          <span className="size-1.5 rounded-full bg-[#2563EB]" />
                          {build.channel}
                        </span>
                      </td>

                      {/* Ukuran */}
                      <td className="py-3 px-4 font-mono text-[#344054]">
                        {formatBytes(build.sizeBytes)}
                      </td>

                      {/* SHA-256 */}
                      <td className="py-3 px-4 max-w-[200px]">
                        <span className="font-mono text-[11px] text-[#667085] break-all leading-tight block select-all">
                          {build.sha256}
                        </span>
                      </td>

                      {/* Diunggah Oleh */}
                      <td className="py-3 px-4 text-[#344054]">
                        {actorName(build.uploadedBy)}
                      </td>

                      {/* Waktu */}
                      <td className="py-3 px-4 text-[#667085] font-mono whitespace-nowrap">
                        {formatDateTime(build.createdAt)}
                      </td>

                      {/* Actions */}
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          {/* Copy Share Link */}
                          <button
                            type="button"
                            onClick={() => void handleCopyLink(build)}
                            className="p-1.5 rounded-md hover:bg-[#EAECF0] text-[#344054] transition-colors"
                            title="Salin tautan pengujian untuk tester"
                          >
                            {copiedId === build.id ? (
                              <Check className="size-3.5 text-[#17B26A]" />
                            ) : (
                              <Copy className="size-3.5" />
                            )}
                          </button>

                          {/* Show QR Code */}
                          <button
                            type="button"
                            onClick={() => void handleShowQr(build)}
                            className="p-1.5 rounded-md hover:bg-[#EAECF0] text-[#344054] transition-colors"
                            title="Tampilkan Kode QR untuk scan di HP"
                          >
                            <QrCode className="size-3.5 text-[#2563EB]" />
                          </button>

                          {/* Direct Download */}
                          <button
                            type="button"
                            onClick={() => void handleDownload(build)}
                            className="p-1.5 rounded-md hover:bg-[#EAECF0] text-[#344054] transition-colors"
                            title="Unduh berkas biner"
                          >
                            <Download className="size-3.5" />
                          </button>

                          {/* Promote to channel target */}
                          {canRelease && build.fingerprint && (
                            <button
                              type="button"
                              onClick={() => setPromoteTarget(build)}
                              className="p-1.5 rounded-md hover:bg-[#EFF4FF] text-[#2563EB] transition-colors"
                              title="Jadikan build ini target aktif kanalnya"
                            >
                              <Target className="size-3.5" />
                            </button>
                          )}

                          {/* Delete */}
                          {canRelease && (
                            <button
                              type="button"
                              onClick={() => setDeleteTarget(build)}
                              className="p-1.5 rounded-md hover:bg-[#FEF3F2] text-[#F04438] transition-colors"
                              title="Hapus biner"
                            >
                              <Trash2 className="size-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pager */}
        {total > PAGE_SIZE && (
          <div className="flex items-center justify-between border-t border-[#EAECF0] px-4 py-3 text-xs text-[#667085]">
            <span>
              Menampilkan {page * PAGE_SIZE + 1}–{Math.min(total, (page + 1) * PAGE_SIZE)} dari {total} biner
            </span>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => scope.set({ page: page - 1 === 0 ? undefined : page - 1 })}
                disabled={page === 0}
                className="h-7 text-xs"
              >
                Sebelumnya
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => scope.set({ page: Math.min(lastPage, page + 1) })}
                disabled={page >= lastPage}
                className="h-7 text-xs"
              >
                Berikutnya
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Upload Binary Modal */}
      {uploadOpen && selectedApp && (
        <UploadBuildModal
          app={selectedApp}
          defaultChannel={scope.channel || channels[0]?.name || "internal-test"}
          onClose={() => setUploadOpen(false)}
        />
      )}

      {/* QR Code Modal */}
      {qrTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-xs p-4">
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl border border-[#EAECF0] text-center">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-base font-bold text-[#101828]">Scan untuk Menginstal</h3>
              <button
                type="button"
                onClick={() => setQrTarget(null)}
                className="rounded p-1 text-[#98A2B3] hover:text-[#101828]"
              >
                <X className="size-4" />
              </button>
            </div>

            <p className="text-xs text-[#667085] mb-4">
              Arahkan kamera ponsel tester ke kode QR berikut untuk mengunduh {qrTarget.build.filename}:
            </p>

            {/* QR Code Container — generated in this browser, see QrImage. */}
            <div className="mx-auto flex size-52 items-center justify-center rounded-xl bg-white border border-[#EAECF0] p-4">
              <QrImage
                value={qrTarget.url}
                alt={`Kode QR untuk mengunduh ${qrTarget.build.filename}`}
                className="size-44 rounded-lg object-contain"
              />
            </div>

            <div className="mt-3 rounded-lg border border-[#EAECF0] bg-[#F6F7F9] p-3 text-left">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-[#667085]">
                  Tautan Langsung Tester
                </span>
                <span className="text-[10px] font-medium text-[#17B26A] bg-[#ECFDF3] border border-[#ABEFC6] px-1.5 py-0.5 rounded">
                  Tanpa Masa Kadaluarsa
                </span>
              </div>
              <p className="text-[11px] font-mono text-[#344054] break-all select-all leading-relaxed">
                {qrTarget.url}
              </p>
            </div>

            <div className="mt-4 flex gap-2">
              <Button
                variant="outline"
                className="flex-1 text-xs"
                onClick={() => {
                  void copyText(qrTarget.url);
                  toast("Tautan disalin (permanen tanpa batas waktu)");
                }}
              >
                <Copy className="size-3.5 mr-1" />
                Salin Tautan
              </Button>
              <Button
                className="flex-1 bg-[#0F1B3D] text-white text-xs"
                onClick={() => setQrTarget(null)}
              >
                Tutup
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-xs p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl border border-[#EAECF0]">
            <h3 className="text-base font-bold text-[#101828]">Hapus Biner Pengujian?</h3>
            <p className="mt-2 text-xs text-[#667085]">
              Apakah Anda yakin ingin menghapus berkas <strong className="text-[#101828] font-mono">{deleteTarget.filename}</strong>? Tindakan ini akan membatalkan tautan pengujian publik yang telah dibagikan.
            </p>
            <div className="mt-5 flex justify-end gap-2.5">
              <Button variant="outline" size="sm" onClick={() => setDeleteTarget(null)}>
                Batal
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => void confirmDelete()}
                disabled={isDeleting}
              >
                {isDeleting ? "Menghapus…" : "Hapus Berkas"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Promote to channel target */}
      {promoteTarget && (
        <PromoteBuildModal build={promoteTarget} onClose={() => setPromoteTarget(null)} />
      )}
    </div>
  );
}

/**
 * Moving a channel's native target is the one action here that changes what
 * every phone on the old binary is told, so the dialog leads with that number
 * instead of asking for a blind confirmation.
 */
function PromoteBuildModal({ build, onClose }: { build: BuildSummary; onClose: () => void }) {
  const toast = useToast();
  const setup = useGetSetupQuery();
  const [promoteBuild, { isLoading }] = usePromoteBuildMutation();
  const [note, setNote] = useState("");
  const [blocking, setBlocking] = useState(false);

  const state = setup.data?.apps
    .find((app) => app.slug === build.app)
    ?.channels.find((channel) => channel.name === build.channel)
    ?.platforms.find((platform) => platform.platform === build.platform);

  const matching =
    state?.fingerprints.find((row) => row.fingerprint === build.fingerprint)?.devices ?? 0;
  const stranded = Math.max(0, (state?.devices ?? 0) - matching);
  const current = state?.target?.fingerprint ?? null;
  const alreadyTarget = current !== null && current === build.fingerprint;

  async function handlePromote() {
    const result = await promoteBuild({ id: build.id, note: note || undefined, blocking });
    if ("error" in result) {
      toast(apiErrorMessage(result.error), "danger");
      return;
    }
    toast(
      result.data.strandedDevices === 0
        ? `Target ${build.channel}/${build.platform} disetel ke build ini.`
        : `Target disetel. ${result.data.strandedDevices} perangkat akan diminta memperbarui aplikasi.`,
    );
    onClose();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-xs p-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl border border-[#EAECF0]">
        <h3 className="text-base font-bold text-[#101828]">Jadikan Target Aktif?</h3>
        <p className="mt-2 text-xs text-[#667085]">
          Kanal <strong className="text-[#101828]">{build.channel}</strong> ({PLATFORM_LABEL[build.platform]}) akan
          menganggap build ini sebagai versi yang sedang diedarkan.
        </p>

        <div className="mt-4 rounded-lg border border-[#EAECF0] bg-[#F6F7F9] p-3 space-y-1.5 text-[11px]">
          <div className="flex justify-between gap-3">
            <span className="text-[#667085]">Fingerprint build ini</span>
            <code className="font-mono text-[#101828] break-all text-right">{build.fingerprint}</code>
          </div>
          <div className="flex justify-between gap-3">
            <span className="text-[#667085]">Target sekarang</span>
            <code className="font-mono text-[#475467] break-all text-right">{current ?? "belum disetel"}</code>
          </div>
        </div>

        {alreadyTarget ? (
          <p className="mt-3 text-xs text-[#667085]">Build ini sudah menjadi target aktif kanal tersebut.</p>
        ) : (
          <div className="mt-3 rounded-lg border border-[#FEDF89] bg-[#FFFAEB] p-3 text-xs text-[#B54708]">
            <strong className="block">
              {stranded.toLocaleString("id-ID")} perangkat akan diminta memperbarui aplikasi.
            </strong>
            Pastikan versi ini sudah benar-benar bisa diunduh — untuk kanal produksi, tunggu sampai tayang di
            Play Store / App Store.
          </div>
        )}

        <div className="mt-4 space-y-1.5">
          <Label htmlFor="promote-note" className="text-xs">
            Pesan untuk pengguna versi lama (opsional)
          </Label>
          <Input
            id="promote-note"
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder="Versi baru sudah tersedia, silakan perbarui aplikasi."
            maxLength={500}
          />
          <p className="text-[11px] text-[#98A2B3]">
            Hanya terbaca oleh aplikasi versi baru; binari yang sudah beredar memakai teksnya sendiri.
          </p>
        </div>

        <label className="mt-3 flex items-start gap-2 text-xs text-[#344054]">
          <input
            type="checkbox"
            checked={blocking}
            onChange={(event) => setBlocking(event.target.checked)}
            className="mt-0.5"
          />
          <span>
            Kunci aplikasi sampai pengguna memperbarui.
            <span className="block text-[11px] text-[#98A2B3]">
              Default mati: banner yang bisa ditutup masih bisa dibatalkan, layar terkunci yang salah pasang
              langsung jadi antrean call center.
            </span>
          </span>
        </label>

        <div className="mt-5 flex justify-end gap-2.5">
          <Button variant="outline" size="sm" onClick={onClose}>
            Batal
          </Button>
          <Button
            size="sm"
            className="bg-[#0F1B3D] text-white"
            onClick={() => void handlePromote()}
            disabled={isLoading}
          >
            {isLoading ? "Menyetel…" : "Jadikan Target Aktif"}
          </Button>
        </div>
      </div>
    </div>
  );
}

interface UploadBuildModalProps {
  app: AppSummary;
  defaultChannel: string;
  onClose: () => void;
}

function UploadBuildModal({ app, defaultChannel, onClose }: UploadBuildModalProps) {
  const toast = useToast();
  const dispatch = useAppDispatch();

  const [file, setFile] = useState<File | null>(null);
  const [channel, setChannel] = useState(defaultChannel);
  const [version, setVersion] = useState("");
  const [buildNumber, setBuildNumber] = useState("");
  const [notes, setNotes] = useState("");
  const [dragActive, setDragActive] = useState(false);

  const derived = file ? deriveBinary(file.name) : null;

  function handleFileDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) {
      setFile(e.dataTransfer.files[0]);
    }
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!file || !derived) {
      toast("Pilih berkas biner .apk, .aab, atau .ipa yang valid", "danger");
      return;
    }
    if (!version.trim()) {
      toast("Masukkan versi native aplikasi", "danger");
      return;
    }

    // No platform/kind: the server derives both from the file extension, and
    // sending them only invited the two to disagree.
    const meta: UploadBuildRequest = {
      app: app.slug,
      channel,
      version: version.trim(),
      buildNumber: buildNumber.trim() || undefined,
      notes: notes.trim() || undefined,
    };

    // Not awaited on purpose: the transfer is owned by the upload manager, so
    // the dialog can close at once and the operator can keep working while a
    // few hundred megabytes go up. Progress and failures surface in the dock.
    void dispatch(startBuildUpload({ binary: file, meta }));
    toast(`Mengunggah ${file.name} di latar belakang — Anda bisa berpindah halaman.`);
    onClose();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-xs p-4">
      <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl border border-[#EAECF0]">
        <div className="flex items-center justify-between pb-3 border-b border-[#EAECF0]">
          <div>
            <h3 className="text-base font-bold text-[#101828]">Unggah Biner Pengujian</h3>
            <p className="text-xs text-[#667085]">
              Biner native untuk pengujian perangkat internal & QA ({app.name})
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-[#98A2B3] hover:text-[#101828]"
          >
            <X className="size-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="mt-4 space-y-4">
          {/* Drag & Drop Zone */}
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setDragActive(true);
            }}
            onDragLeave={() => setDragActive(false)}
            onDrop={handleFileDrop}
            className={`flex flex-col items-center justify-center rounded-xl border-2 border-dashed p-6 text-center transition-colors ${
              dragActive
                ? "border-[#2563EB] bg-[#EFF4FF]"
                : file
                ? "border-[#ABEFC6] bg-[#ECFDF3]"
                : "border-[#D0D5DD] hover:border-[#98A2B3] bg-[#F6F7F9]"
            }`}
          >
            {file ? (
              <div className="space-y-1">
                <div className="mx-auto size-10 rounded-full bg-[#17B26A] text-white flex items-center justify-center">
                  <Check className="size-5" />
                </div>
                <p className="font-semibold text-xs text-[#101828] font-mono">{file.name}</p>
                <p className="text-[11px] text-[#667085]">
                  {formatBytes(file.size)} ·{" "}
                  <span className="font-bold text-[#17B26A] uppercase">
                    {derived ? `${derived.kind} (${PLATFORM_LABEL[derived.platform]})` : "Format tidak didukung"}
                  </span>
                </p>
                <button
                  type="button"
                  onClick={() => setFile(null)}
                  className="text-xs text-[#F04438] hover:underline pt-1"
                >
                  Ganti berkas
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <UploadCloud className="mx-auto size-9 text-[#98A2B3]" />
                <p className="text-xs text-[#101828]">
                  Tarik berkas kemari atau{" "}
                  <label className="text-[#2563EB] font-semibold hover:underline cursor-pointer">
                    pilih dari komputer
                    <input
                      type="file"
                      accept=".apk,.aab,.ipa"
                      className="hidden"
                      onChange={(e) => {
                        if (e.target.files?.[0]) setFile(e.target.files[0]);
                      }}
                    />
                  </label>
                </p>
                <div className="flex items-center justify-center gap-1.5 pt-1">
                  <span className="text-[10px] font-semibold bg-white border border-[#D0D5DD] px-1.5 py-0.5 rounded text-[#344054]">
                    .APK
                  </span>
                  <span className="text-[10px] font-semibold bg-white border border-[#D0D5DD] px-1.5 py-0.5 rounded text-[#344054]">
                    .AAB
                  </span>
                  <span className="text-[10px] font-semibold bg-white border border-[#D0D5DD] px-1.5 py-0.5 rounded text-[#344054]">
                    .IPA
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Form Fields */}
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div className="space-y-1">
              <Label className="text-xs text-[#344054]">Kanal Pengujian</Label>
              <select
                value={channel}
                onChange={(e) => setChannel(e.target.value)}
                className="w-full h-8.5 rounded-lg border border-[#D0D5DD] bg-white px-2.5 text-xs text-[#101828] focus:border-[#2563EB] focus:outline-none"
              >
                {app.channels.map((ch) => (
                  <option key={ch.id} value={ch.name}>
                    {ch.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-[#344054]">Versi Native *</Label>
              <Input
                placeholder="mis. 2.4.2"
                value={version}
                onChange={(e) => setVersion(e.target.value)}
                className="h-8.5 text-xs border-[#D0D5DD]"
                required
              />
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-[#344054]">Nomor Build (opsional)</Label>
              <Input
                placeholder="mis. 142"
                value={buildNumber}
                onChange={(e) => setBuildNumber(e.target.value)}
                className="h-8.5 text-xs border-[#D0D5DD]"
              />
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-[#344054]">Target Platform</Label>
              <Input
                disabled
                value={derived ? `${PLATFORM_LABEL[derived.platform]} (${derived.kind.toUpperCase()})` : "Otomatis dari berkas"}
                className="h-8.5 text-xs bg-[#F6F7F9] border-[#EAECF0]"
              />
            </div>
          </div>

          <div className="space-y-1">
            <Label className="text-xs text-[#344054]">Catatan Pengujian (opsional)</Label>
            <Input
              placeholder="mis. Testing hotfix auth token & integrasi QRIS"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="h-8.5 text-xs border-[#D0D5DD]"
            />
          </div>

          <div className="flex justify-end gap-2.5 pt-2 border-t border-[#EAECF0]">
            <Button variant="outline" size="sm" type="button" onClick={onClose}>
              Batal
            </Button>
            <Button
              size="sm"
              type="submit"
              disabled={!file || !version}
              className="bg-[#0F1B3D] hover:bg-[#1D2939] text-white text-xs"
            >
              Unggah & Bagikan
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
